<template>
  <div
    class="floatMenu bottom-20px z-999 absolute flex items-center justify-start"
    @mouseenter="handleOver"
  >
    <Popover
      placement="leftTop"
      trigger="click"
      :destroy-tooltip-on-hide="true"
      :open="opened"
      :arrow="false"
      :overlayInnerStyle="{
        padding: 0,
      }"
      @open-change="openChange"
    >
      <div
        ref="dragRef"
        class="dragContanier w-40px h-40px flex items-center justify-center rounded-[50%] bg-white text-[#01a887] shadow shadow-gray-300"
        :style="style"
        style="position: fixed; z-index: 111"
      >
        <MIcon prefix="g" icon="float-menu" />
      </div>
      <template #content>
        <div class="w-200px p-12px leading-24px">
          <div class="header mb-12px flex items-center justify-between">
            <span class="text-14px font-600">常用功能</span>
            <span class="cursor-pointer">
              <SettingOutlined :style="{ fontSize: '16px' }" @click="() => openModal(true, {})" />
            </span>
          </div>
          <div
            v-for="item in collectMenu"
            :key="item.name"
            class="mb-8px flex cursor-pointer items-center"
            :class="[
              {
                [`path-active`]: currentRoute?.path === item.path,
              },
            ]"
            @click="jumpPath(item.path)"
          >
            <MIcon :icon="item.icon || 'dingbucaidan-kuaijierukou'" :size="18" class="mr-8px" />
            <span>{{ t(item.name) }}</span>
          </div>
        </div>
      </template>
    </Popover>

    <MenuEditModal @register="registerModal" />
  </div>
</template>
<script setup lang="ts">
  import { SettingOutlined } from '@ant-design/icons-vue';
  import { onClickOutside, useDraggable, useResizeObserver, useWindowSize } from '@vueuse/core';
  import { Popover } from 'ant-design-vue';
  import { onMounted, ref, watch } from 'vue';
  import type { RouteLocationNormalized } from 'vue-router';
  import { useModal } from '@mts/components/Modal';
  import { t } from '@mts/hooks/web/useI18n';
  import { useGo } from '@mts/hooks/web/usePage';
  import { listenerRouteChange } from '@mts/logics/mitt/routeChange';
  import API, { parseConfigValue } from '@/api/ims-system';
  import { getMenus } from '@/router/menus';
  import type { Menu } from '@/router/types';
  import { getPermissionStore } from '@/store';
  import MenuEditModal from '@/views/common/CSRender/fengji/FastMenu/src/MenuEditModal.vue';

  const go = useGo();
  defineProps({
    //最多展示菜单数量
    maxCount: {
      type: Number,
      default: 8,
    },
  });

  // Menu changes
  const permissionStore = getPermissionStore();
  let menuModules = ref<Menu[]>([]);

  watch(
    [() => permissionStore.getLastBuildMenuTime, () => permissionStore.getBackMenuList],
    () => {
      menuModules.value = getMenus();
    },
    { immediate: true },
  );

  const currentRoute = ref<RouteLocationNormalized | null>(null);
  listenerRouteChange((route) => {
    currentRoute.value = route;
  });

  const jumpPath = (path: string) => {
    go(path);
  };

  const [registerModal, { openModal }] = useModal();

  const dragRef = ref<HTMLElement | null>(null);
  const moving = ref(false);
  const { width, height } = useWindowSize();
  //@ts-ignore
  const { style, position } = useDraggable(dragRef, {
    initialValue: {
      x: width.value - 20,
      y: height.value - 80,
    },
    axis: 'y',
    preventDefault: true,
    stopPropagation: true,
    onStart: () => {
      const dom = document.getElementsByClassName('dragContanier')[0] as HTMLElement;
      dom.style.transition = 'none';
    },
    onMove: (position) => {
      moving.value = true;
      if (position.y < 0) {
        position.y = 0;
      }
      if (position.y > height.value - 40) {
        position.y = height.value - 40;
      }
    },
    onEnd: (position) => {
      moving.value = false;
      const dom = document.getElementsByClassName('dragContanier')[0] as HTMLElement;
      dom.style.transition = 'all 0.5s ease';
      const percent = position.y / height.value;
      localStorage.setItem('menuPosition', `${percent}`);
    },
  });

  const handleOver = () => {
    position.value.x = width.value - 50;
    const dom = document.getElementsByClassName('floatMenu')[0] as HTMLElement;
    if (dom) {
      dom.style.right = '-50px';
    }
  };

  onMounted(() => {
    menuModules.value = getMenus();
    const yPostion = localStorage.getItem('menuPosition');
    if (yPostion) {
      position.value.y = Number(localStorage.getItem('menuPosition')) * height.value;
    }
  });

  //设置弹窗显示隐藏逻辑
  const opened = ref(false);
  const openChange = (visible: boolean) => {
    opened.value = visible;
  };

  watch(
    () => opened.value,
    () => {
      if (opened.value) {
        getCacheList();
      } else {
        position.value.x = width.value - 20;
      }
    },
    { immediate: true },
  );

  //@ts-ignore
  onClickOutside(dragRef, () => {
    position.value.x = width.value - 20;
  });

  //添加收藏菜单
  const collectMenu = ref<Menu[]>([]);

  //获取当前收藏的菜单
  const getCacheList = async () => {
    const data = await API.getSysConfig({ configKey: 'ims-collect-menu' }).then(parseConfigValue);
    if (!Array.isArray(data)) return;
    collectMenu.value = data;
  };

  useResizeObserver(document.body, () => {
    const yPostion = localStorage.getItem('menuPosition');
    if (yPostion) {
      position.value.y = Number(localStorage.getItem('menuPosition')) * height.value;
    }
    position.value.x = width.value - 20;
  });
</script>
<style lang="less" scoped>
  .dragContanier {
    transition: all 0.5s ease;
    cursor: pointer;
  }

  .path-active {
    color: @primary-color;
  }

  .menuBox {
    .header {
      padding: 0 12px;
      border-bottom: 1px solid #dfe5ec;
    }

    .content {
      height: 500px;
      border-bottom: 1px solid #dfe5ec;

      .left {
        padding: 12px;
        border-right: 1px solid #dfe5ec;
      }

      .right {
        height: 100%;
        padding: 12px;
        overflow-y: auto;
      }
    }

    .footer {
      height: 48px;
      padding: 0 12px;
    }
  }
</style>
